// ─── fetchClientsStatus.js ──────────────────────────────────────────────
// Récupère en 1 seul HTTP le statut "Invitée / Connectée / Active" pour
// une liste de clientes. Cache mémoire 60s pour éviter de re-fetcher
// pendant qu'Anissa scroll/filtre.
//
// Statut "absent" = la cliente n'a pas encore d'app activée (Anissa n'a
// pas publié son plan dans l'app cliente). Affiché en discret côté UI.
//
// V94.66 : input = liste d'objets { email, stagingClientId? } pour
// supporter le matching robuste par client_id (App Store/Play Store où
// l'email Apple/Google peut différer de l'email du SaaS via
// hide-my-email). Si stagingClientId est connu, on l'envoie au backend
// en client_ids[] et on indexe la réponse par stagingClientId. Sinon on
// fallback sur le lookup historique par email.
// L'ancien input string[] reste accepté (rétrocompat).

// V96.35 : passe par le proxy server-side `/api/client-app-proxy`
import { clientAppFetch, checkClientAppConfig } from "./clientAppFetch";

const CACHE_TTL_MS = 60 * 1000;
// Cache key composite : "id:<stagingClientId>" prioritaire, sinon
// "email:<email>". Permet aux 2 méthodes de coexister sans collision et
// d'éviter qu'une lookup "email" périmée écrase une lookup "id" fraîche.
const cache = new Map(); // cacheKey -> { entry, ts }

/** Vide le cache (utile pour debug / tests). */
export function clearStatusCache() {
  cache.clear();
}

/** Normalise un input mixte (strings ou objets) en {email, stagingClientId}. */
function normalizeInput(items) {
  if (!Array.isArray(items)) return [];
  const out = [];
  for (const it of items) {
    if (it == null) continue;
    if (typeof it === "string") {
      const email = it.trim().toLowerCase();
      if (email) out.push({ email, stagingClientId: null });
    } else if (typeof it === "object") {
      const email = String(it.email || "").trim().toLowerCase();
      const stagingClientId = it.stagingClientId
        ? String(it.stagingClientId).trim()
        : null;
      if (email || stagingClientId) {
        out.push({ email: email || null, stagingClientId: stagingClientId || null });
      }
    }
  }
  return out;
}

function cacheKeyFor(item) {
  if (item.stagingClientId) return `id:${item.stagingClientId}`;
  if (item.email) return `email:${item.email}`;
  return null;
}

/**
 * @param {Array<string | { email?: string, stagingClientId?: string }>} items
 * @returns {Promise<Record<string, object>>} map keyed by lowercased email
 *   (et aussi par stagingClientId si fourni) → { status, last_login_at, ... }
 *
 *   Pour rester rétrocompat avec les consommateurs existants, le résultat
 *   contient les entrées indexées par email ; pour les items qui n'ont pas
 *   d'email mais juste un stagingClientId, l'entrée est aussi accessible
 *   via la clé `id:<stagingClientId>`.
 */
export async function fetchClientsStatus(items, { strict = false } = {}) {
  // V96.35 : check basique via helper (URL only). Le secret est server-side.
  if (!checkClientAppConfig().ok) {
    if (strict) throw new Error("Verification app cliente non configuree");
    return {};
  }

  const normalized = normalizeInput(items);
  if (normalized.length === 0) {
    if (strict) throw new Error("Identite cliente manquante pour la verification");
    return {};
  }

  // 1. Sépare ce qu'on a déjà en cache
  const now = Date.now();
  const result = {};
  const toFetch = [];
  for (const item of normalized) {
    const key = cacheKeyFor(item);
    if (!key) continue;
    const cached = cache.get(key);
    if (cached && now - cached.ts < CACHE_TTL_MS) {
      if (item.email) result[item.email] = cached.entry;
      result[key] = cached.entry;
    } else {
      toFetch.push(item);
    }
  }
  if (toFetch.length === 0) return result;

  // 2. Construit le payload : client_ids[] prioritaires (matching robuste),
  //    emails[] en fallback pour les clientes sans staging id connu.
  const clientIdsToFetch = [];
  const emailsToFetch = [];
  for (const item of toFetch) {
    if (item.stagingClientId) clientIdsToFetch.push(item.stagingClientId);
    else if (item.email) emailsToFetch.push(item.email);
  }

  const payload = {};
  if (emailsToFetch.length > 0) payload.emails = emailsToFetch;
  if (clientIdsToFetch.length > 0) payload.client_ids = clientIdsToFetch;

  let body = null;
  try {
    // Simple lecture de statut : elle bloque l'affichage du bouton Publier,
    // donc elle doit abandonner bien avant le délai généreux d'une écriture.
    body = await clientAppFetch("/api/admin/clients-status", {
      method: "POST",
      payload,
      timeoutMs: 15000,
    });
  } catch (error) {
    // network ou config error → on renvoie ce qu'on a (en cache + vide pour le reste)
    if (strict) throw error;
    return result;
  }
  if (!body?.ok) {
    if (strict) throw new Error("Verification app cliente indisponible");
    return result;
  }
  if (strict && body.status_contract_version !== 2) {
    throw new Error("Version de verification app cliente incompatible");
  }

  const statuses = (body.statuses && typeof body.statuses === "object") ? body.statuses : {};
  const statusesById = (body.statuses_by_id && typeof body.statuses_by_id === "object")
    ? body.statuses_by_id
    : {};

  // V97.35 — defauts FAIL-CLOSED pour la visibilite plan : si le backend ne
  // renvoie pas les champs (ancien deploiement, reseau KO, reponse partielle),
  // on n'affiche JAMAIS "visible" a tort. plan_visible/visible_now = false par
  // defaut. reason_if_not_visible reste null tant qu'on ne sait pas (l'UI
  // affichera "Statut inconnu" plutot qu'une fausse certitude).
  const PLAN_DEFAULTS = {
    account_enabled: false,
    account_deleted: false,
    plan_visible: false,
    visible_now: false,
    plan_status: null,
    effective_at: null,
    published_at: null,
    client_id: null,
    plan_id: null,
    source_consultation_id: null,
    published_version: null,
    reason_if_not_visible: null,
  };

  const ABSENT = {
    status: "absent",
    last_login_at: null,
    last_activity_at: null,
    last_reviewed_at: null,
    feedbacks_7d_count: 0,
    new_feedbacks_count: 0,
    found: false,
    ...PLAN_DEFAULTS,
  };

  const CONNECTION_STATUSES = new Set(["active", "connected", "invited", "absent"]);
  const PLAN_STATUSES = new Set(["draft", "published", "archived"]);
  const VISIBILITY_REASONS = new Set([
    "no_plan",
    "draft_only",
    "scheduled_future",
    "account_disabled",
    "account_deleted",
  ]);
  const STRICT_STATUS_KEYS = new Set([
    "status",
    "last_login_at",
    "last_activity_at",
    "last_reviewed_at",
    "feedbacks_7d_count",
    "new_feedbacks_count",
    "found",
    "journey",
    "account_enabled",
    "account_deleted",
    "plan_visible",
    "visible_now",
    "plan_status",
    "effective_at",
    "published_at",
    "client_id",
    "plan_id",
    "source_consultation_id",
    "published_version",
    "reason_if_not_visible",
  ]);

  function nullableString(value) {
    return typeof value === "string" && value.trim() ? value : null;
  }

  function hardenJourney(value) {
    if (!value || typeof value !== "object" || Array.isArray(value)) return null;
    return {
      status: nullableString(value.status),
      questionnaire_completed_at: nullableString(value.questionnaire_completed_at),
      rdv_scheduled_at: nullableString(value.rdv_scheduled_at),
      rdv_done_at: nullableString(value.rdv_done_at),
      analyses_received_at: nullableString(value.analyses_received_at),
      analyses_uploaded_at: nullableString(value.analyses_uploaded_at),
      program_published_at: nullableString(value.program_published_at),
      questionnaire_external_url: nullableString(value.questionnaire_external_url),
      protocol_shipped_at: nullableString(value.protocol_shipped_at),
      protocol_received_at: nullableString(value.protocol_received_at),
    };
  }

  function isNullableString(value) {
    return value === null || (typeof value === "string" && Boolean(value.trim()));
  }

  function isAuthoritativeStatusEntry(value) {
    if (!value || typeof value !== "object" || Array.isArray(value)) return false;
    const keys = Object.keys(value);
    if (keys.length !== STRICT_STATUS_KEYS.size || keys.some((key) => !STRICT_STATUS_KEYS.has(key))) {
      return false;
    }
    if (!CONNECTION_STATUSES.has(value.status)
        || typeof value.found !== "boolean"
        || typeof value.account_enabled !== "boolean"
        || typeof value.account_deleted !== "boolean"
        || typeof value.plan_visible !== "boolean"
        || typeof value.visible_now !== "boolean"
        || !Number.isFinite(value.feedbacks_7d_count)
        || !Number.isFinite(value.new_feedbacks_count)
        || !isNullableString(value.last_login_at)
        || !isNullableString(value.last_activity_at)
        || !isNullableString(value.last_reviewed_at)
        || !isNullableString(value.effective_at)
        || !isNullableString(value.published_at)
        || !isNullableString(value.client_id)
        || !isNullableString(value.plan_id)
        || !isNullableString(value.source_consultation_id)
        || !(value.plan_status === null || PLAN_STATUSES.has(value.plan_status))
        || !(value.reason_if_not_visible === null || VISIBILITY_REASONS.has(value.reason_if_not_visible))
        || !(value.published_version === null
          || (Number.isInteger(value.published_version) && value.published_version >= 1))) {
      return false;
    }
    if (value.found === true && typeof value.client_id !== "string") return false;
    if (value.plan_visible === true) {
      return typeof value.plan_id === "string"
        && typeof value.source_consultation_id === "string"
        && typeof value.published_at === "string"
        && value.plan_status !== null
        && Number.isInteger(value.published_version);
    }
    return true;
  }

  function harden(entry) {
    const e = entry || { ...ABSENT };
    // DTO exact : une reponse admin enrichie ne traverse jamais la frontiere.
    // En particulier, aucun contenu de plan ni champ clinique n'est conserve.
    return {
      status: CONNECTION_STATUSES.has(e.status) ? e.status : "absent",
      last_login_at: nullableString(e.last_login_at),
      last_activity_at: nullableString(e.last_activity_at),
      last_reviewed_at: nullableString(e.last_reviewed_at),
      feedbacks_7d_count: Number.isFinite(e.feedbacks_7d_count) ? e.feedbacks_7d_count : 0,
      new_feedbacks_count: Number.isFinite(e.new_feedbacks_count) ? e.new_feedbacks_count : 0,
      found: e.found === true,
      journey: hardenJourney(e.journey),
      account_enabled: e.account_enabled === true,
      account_deleted: e.account_deleted === true,
      plan_visible: e.plan_visible === true,
      visible_now: e.visible_now === true,
      plan_status: PLAN_STATUSES.has(e.plan_status) ? e.plan_status : null,
      effective_at: nullableString(e.effective_at),
      published_at: nullableString(e.published_at),
      client_id: nullableString(e.client_id),
      plan_id: nullableString(e.plan_id),
      source_consultation_id: nullableString(e.source_consultation_id),
      published_version: Number.isInteger(e.published_version) && e.published_version >= 1
        ? e.published_version
        : null,
      reason_if_not_visible: VISIBILITY_REASONS.has(e.reason_if_not_visible)
        ? e.reason_if_not_visible
        : null,
    };
  }

  // 3. Cache + merge. Pour chaque item, on prend la lookup id en priorité
  //    (plus fiable), avec fallback sur email.
  for (const item of toFetch) {
    let entry = null;
    if (item.stagingClientId && statusesById[item.stagingClientId]) {
      entry = statusesById[item.stagingClientId];
    } else if (item.email && statuses[item.email]) {
      entry = statuses[item.email];
    }
    if (strict && !entry) {
      throw new Error("Reponse de verification app cliente incomplete");
    }
    if (strict && !isAuthoritativeStatusEntry(entry)) {
      throw new Error("Schema de verification app cliente invalide");
    }
    entry = harden(entry);

    const key = cacheKeyFor(item);
    if (key) cache.set(key, { entry, ts: now });

    if (item.email) result[item.email] = entry;
    if (key) result[key] = entry;
  }
  return result;
}
